import type { Formatters } from "../../i18n/format";
import { translateKey, type Translate } from "../../i18n/translate";

/**
 * Small formatting helpers shared between `ChannelSchedulePreview` and
 * `EpgEventDetailDialog` - split out so the detail dialog does not have to
 * import from the panel (a schedule row opening a dialog that imports back
 * from the panel that renders it is a circular import waiting to bite the
 * next change to either file). They take the `useFormat()` result and `t`
 * because the same text is built outside render too.
 */

export function clock(fmt: Formatters, iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "??:??";
  return fmt.time(ms, { hour: "2-digit", minute: "2-digit" });
}

export function day(fmt: Formatters, iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  return fmt.date(ms, { weekday: "short", day: "numeric", month: "short" });
}

/** Both instants, stated - never "today", which this data cannot promise. */
export function stamp(fmt: Formatters, iso: string): string {
  return `${day(fmt, iso)} ${clock(fmt, iso)}`;
}

export function span(t: Translate, seconds: number): string {
  const k = (name: string, params: Record<string, number>) =>
    translateKey(t, "channels:duration." + name, params);
  if (seconds < 60)
    return k("seconds", { count: Math.max(0, Math.round(seconds)) });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return k("minutes", { count: minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest
    ? k("hoursMinutes", { hours, minutes: rest })
    : k("hours", { count: hours });
}
